import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { MobileCard } from "../components/MobileCard";
import type { BlogPost } from "../../modules/blog/mock";

const statusColorMap: Record<BlogPost["status"], string> = {
  公開: "green",
  下書き: "gray",
  予約公開: "blue",
  非公開: "red",
};

const sampleBody = `近年、AI を活用したマーケティングは中小企業にとっても身近な存在になりました。
本記事では、AI ツールの導入効果と運用上の注意点を解説します。

## 1. AI で変わるマーケティング業務
コンテンツ生成 / データ分析 / 顧客対応の 3 領域で大きな効果が期待できます。

## 2. 導入時の 3 つの注意点
- 出力の事実確認は必ず人間が行う
- ブランドトーンを保つための社内プロンプト整備
- 個人情報・機密情報の入力禁止

## 3. まとめ
AI は「人間の代替」ではなく「人間の拡張」。適切に活用すれば少人数でも大きな成果を上げられます。`;

/**
 * モバイルブログ編集ページ
 * タイトル + メタ情報 + ツールバー + 本文 + 保存 / AI 改善
 */
export const MobileBlogEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 一覧画面から渡された記事データ（無ければモック 1 件目）
  const post = (location.state as BlogPost | null) ?? null;

  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(sampleBody);
  const [toast, setToast] = useState<string | null>(null);

  // 軽量 Toast（3 秒で自動消滅）
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2000);
  };

  return (
    <>
      <header className="mobile-appbar">
        <button
          type="button"
          className="mobile-appbar-icon-btn"
          onClick={() => navigate(-1)}
          aria-label="戻る"
        >
          <ArrowLeft size={20} strokeWidth={2} />
        </button>
        <h1 className="mobile-appbar-title">記事編集</h1>
        <div style={{ width: 36 }} />
      </header>
      <main className="mobile-content">
        <MobileCard>
          {/* タイトル */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="タイトル"
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 17,
              fontWeight: 700,
              color: "#0f172a",
              fontFamily: "inherit",
              padding: "4px 0 12px 0",
              boxSizing: "border-box",
            }}
          />
          {/* メタ情報 */}
          {post && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                paddingBottom: 12,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span className={`mobile-badge ${statusColorMap[post.status]}`}>
                {post.status}
              </span>
              <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                {post.category}
              </span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                {post.author}
              </span>
            </div>
          )}

          {/* ツールバー */}
          <div className="mobile-edit-toolbar">
            <button type="button" className="mobile-edit-tool" aria-label="太字">
              <strong>B</strong>
            </button>
            <button type="button" className="mobile-edit-tool" aria-label="斜体">
              <em>I</em>
            </button>
            <button type="button" className="mobile-edit-tool" aria-label="リンク">
              🔗
            </button>
            <button
              type="button"
              className="mobile-edit-tool ai"
              aria-label="AI 補助"
            >
              <Sparkles size={16} strokeWidth={2} />
            </button>
          </div>

          {/* 本文 */}
          <textarea
            className="mobile-edit-area"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="本文を入力…"
          />
        </MobileCard>

        {/* アクションボタン */}
        <button
          type="button"
          className="mobile-btn mobile-btn-secondary mobile-btn-block"
          onClick={() => showToast("AI が本文を改善しました")}
        >
          <Sparkles size={16} strokeWidth={2} />
          AI で改善
        </button>
        <button
          type="button"
          className="mobile-btn mobile-btn-primary mobile-btn-block"
          onClick={() => showToast("保存しました")}
        >
          保存
        </button>
      </main>

      {toast && (
        <div className="mobile-toast" role="status">
          <Sparkles size={14} strokeWidth={2.5} />
          {toast}
        </div>
      )}
    </>
  );
};
